import { useRef, useState } from 'react'
import { Input } from 'react-daisyui'
import { IconPhoto, IconX } from '@tabler/icons-react'
import { Button } from '@/components/Button.tsx'

/** Public contact fields shared by the account default contact and the "Marken" */
export type ContactFieldKey =
  | 'contact_email'
  | 'contact_phone'
  | 'contact_website'
  | 'contact_instagram'
  | 'contact_facebook'
  | 'contact_twitter'
  | 'contact_tiktok'
  | 'contact_youtube'
  | 'contact_pinterest'
  | 'contact_google'

/** Only present on "Marken" — the default contact takes these from the billing address */
export type ContactIdentityKey = 'name' | 'company' | 'first_name' | 'last_name'

type ContactValueKey = ContactFieldKey | ContactIdentityKey

const CONTACT_FIELDS: Array<{ key: ContactFieldKey, label: string, placeholder: string, type?: string }> = [
  { key: 'contact_email', label: 'E-Mail', placeholder: 'kontakt@beispiel.de', type: 'email' },
  { key: 'contact_phone', label: 'Telefon', placeholder: '+49 123 456789', type: 'tel' },
  { key: 'contact_website', label: 'Website', placeholder: 'https://www.beispiel.de', type: 'url' },
  { key: 'contact_instagram', label: 'Instagram', placeholder: 'https://instagram.com/beispiel' },
  { key: 'contact_facebook', label: 'Facebook', placeholder: 'https://facebook.com/beispiel' },
  { key: 'contact_twitter', label: 'X (Twitter)', placeholder: 'https://x.com/beispiel' },
  { key: 'contact_tiktok', label: 'TikTok', placeholder: 'https://tiktok.com/@beispiel' },
  { key: 'contact_youtube', label: 'YouTube', placeholder: 'https://youtube.com/@beispiel' },
  { key: 'contact_pinterest', label: 'Pinterest', placeholder: 'https://pinterest.com/beispiel' },
  { key: 'contact_google', label: 'Google', placeholder: 'https://g.page/beispiel', type: 'url' },
]

export type ContactFormValues = Partial<Record<ContactValueKey, string>> & {
  contact_image?: string | null
}

export type ContactFormSubmit = Partial<Record<ContactValueKey, string>> & {
  contact_image?: File
  contact_image_clear?: boolean
}

interface ContactFormProps {
  initialData: ContactFormValues
  /** Adds name (internal label), company, first and last name — used for "Marken" */
  withIdentityFields?: boolean
  /** Rejecting keeps the pending image change so the user can retry */
  onSubmit: (data: ContactFormSubmit) => Promise<void>
  isLoading?: boolean
  submitLabel?: string
  onCancel?: () => void
}

/**
 * Contact form of the default contact (settings → "Kontakt") and of a "Marke".
 * All values are trimmed; the image is only sent when it changed.
 */
export default function ContactForm({
  initialData,
  withIdentityFields,
  onSubmit,
  isLoading,
  submitLabel = 'Speichern',
  onCancel,
}: ContactFormProps) {
  const [values, setValues] = useState<Partial<Record<ContactValueKey, string>>>(() => {
    const { contact_image: _, ...rest } = initialData
    return rest
  })
  const [imagePreview, setImagePreview] = useState<string | null>(initialData.contact_image || null)
  const [imageFile, setImageFile] = useState<File | null>(null)
  const [imageClear, setImageClear] = useState(false)
  const imageInputRef = useRef<HTMLInputElement>(null)

  function setValue(key: ContactValueKey, value: string) {
    setValues(prev => ({ ...prev, [key]: value }))
  }

  function handleImageSelect(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0]
    if (!file) return
    setImageFile(file)
    setImageClear(false)
    setImagePreview(URL.createObjectURL(file))
    e.target.value = ''
  }

  function handleImageRemove() {
    setImageFile(null)
    setImageClear(true)
    setImagePreview(null)
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    if (withIdentityFields && !values.name?.trim()) return

    const keys: ContactValueKey[] = [
      ...(withIdentityFields ? ['name', 'company', 'first_name', 'last_name'] as ContactIdentityKey[] : []),
      ...CONTACT_FIELDS.map(f => f.key),
    ]
    const data: ContactFormSubmit = Object.fromEntries(keys.map(key => [key, (values[key] || '').trim()])) as ContactFormSubmit
    if (imageFile) data.contact_image = imageFile
    if (imageClear) data.contact_image_clear = true

    try {
      await onSubmit(data)
      setImageFile(null)
      setImageClear(false)
    } catch {
      // The caller reports the error; keep the pending image change for a retry
    }
  }

  function renderInput(key: ContactValueKey, label: string, placeholder: string, type?: string, required?: boolean) {
    return (
      <label key={key} className="form-control w-full">
        <div className="label">
          <span className="label-text">{label}</span>
        </div>
        <Input
          type={type}
          value={values[key] || ''}
          onChange={e => setValue(key, e.target.value)}
          placeholder={placeholder}
          required={required}
        />
      </label>
    )
  }

  return (
    <form onSubmit={handleSubmit} className="flex flex-col gap-4">
      {withIdentityFields && (
        <>
          <label className="form-control w-full">
            <div className="label">
              <span className="label-text">Bezeichnung</span>
            </div>
            <Input
              value={values.name || ''}
              onChange={e => setValue('name', e.target.value)}
              placeholder="z. B. Tierfotografie"
              maxLength={128}
              required
              autoFocus
            />
            <div className="label">
              <span className="label-text-alt opacity-60">Nur für dich zur Unterscheidung — wird Kunden nicht angezeigt.</span>
            </div>
          </label>
          {renderInput('company', 'Firma', 'Musterfoto GmbH')}
          <div className="flex flex-col sm:flex-row gap-4">
            {renderInput('first_name', 'Vorname', 'Max')}
            {renderInput('last_name', 'Nachname', 'Mustermann')}
          </div>
        </>
      )}

      {/* Contact image */}
      <div className="form-control w-full">
        <div className="label">
          <span className="label-text">Profilbild</span>
        </div>
        <div className="flex items-center gap-4">
          <div
            className="w-20 h-20 rounded-full bg-secondary flex items-center justify-center overflow-hidden flex-shrink-0 cursor-pointer"
            onClick={() => imageInputRef.current?.click()}
          >
            {imagePreview ? (
              <img src={imagePreview} alt="Profilbild" className="w-full h-full object-cover" />
            ) : (
              <IconPhoto size={28} className="opacity-40" />
            )}
          </div>
          <div className="flex flex-col gap-1">
            <Button type="button" size="sm" color="ghost" onClick={() => imageInputRef.current?.click()}>
              {imagePreview ? 'Ändern' : 'Hochladen'}
            </Button>
            {imagePreview && (
              <Button type="button" size="sm" color="ghost" className="text-error" onClick={handleImageRemove}>
                <IconX size={14} />
                Entfernen
              </Button>
            )}
          </div>
        </div>
        <input
          ref={imageInputRef}
          type="file"
          accept="image/*"
          className="hidden"
          onChange={handleImageSelect}
        />
      </div>

      {CONTACT_FIELDS.map(f => renderInput(f.key, f.label, f.placeholder, f.type))}

      <div className="flex flex-row gap-3">
        {onCancel && (
          <Button type="button" color="ghost" disabled={isLoading} onClick={onCancel}>
            Abbrechen
          </Button>
        )}
        <Button type="submit" color="primary" className={onCancel ? 'flex-1' : 'w-full'}
          loading={isLoading} disabled={isLoading || (withIdentityFields && !values.name?.trim())}>
          {submitLabel}
        </Button>
      </div>
    </form>
  )
}
